import React from "react";
import { CalendarDays, CalendarCheck, Droplets, Car, Search, Check, HelpCircle, Star, Sparkles } from "lucide-react";
import PalcoEscalavel from "../multichannel/PalcoEscalavel";
import { WhatsAppIcon } from "../multichannel/iconesCanais";

/**
 * FluxoCanvasLavaJato — layout dedicado do Lava Jato.
 *
 * Storytelling visual: foto do cliente, robo 3D grande, foto do carro limpo.
 * Fluxo especifico do nicho: escolha do servico + reserva de box + execucao + entrega.
 *
 * Palco 1500x500 com 8 elementos posicionados absolutamente.
 */

const PALCO_L = 1500;
const PALCO_A = 500;

const CAIXA_BASE = {
  background: "linear-gradient(160deg, rgba(28,33,32,0.95) 0%, rgba(16,20,19,0.98) 100%)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "16px",
  boxShadow: "0 20px 44px -18px rgba(0,0,0,0.95)",
};

const CAIXA_ATIVA = {
  ...CAIXA_BASE,
  border: "1px solid rgba(34,197,94,0.5)",
  boxShadow: "0 20px 44px -18px rgba(0,0,0,0.95), 0 0 30px -10px rgba(34,197,94,0.4)",
};

function Card({ x, y, w, h, children, ativa = false, style = {}, className = "", ...resto }) {
  return (
    <div
      className={`absolute overflow-hidden ${className}`.trim()}
      style={{
        left: x, top: y, width: w, height: h,
        ...(ativa ? CAIXA_ATIVA : CAIXA_BASE),
        ...style,
      }}
      {...resto}
    >
      {children}
    </div>
  );
}

export default function FluxoCanvasLavaJato() {
  return (
    <PalcoEscalavel largura={PALCO_L} altura={PALCO_A}>
      {/* Halo de fundo */}
      <div
        className="pointer-events-none absolute rounded-full"
        style={{
          left: 400, top: 100, width: 800, height: 300,
          background: "radial-gradient(ellipse, rgba(34,197,94,0.10) 0%, transparent 68%)",
          filter: "blur(80px)",
        }}
      />

      {/* Conexoes SVG */}
      <svg
        className="absolute inset-0 pointer-events-none"
        width={PALCO_L} height={PALCO_A}
        viewBox={`0 0 ${PALCO_L} ${PALCO_A}`}
        aria-hidden="true"
      >
        {/* Cliente -> IA */}
        <path className="av-conector" data-ordem={2} d="M210 200 L230 200" stroke="rgba(34,197,94,0.6)" strokeWidth="2" fill="none" />
        <circle className="av-conector" data-ordem={2} cx="220" cy="200" r="3" fill="#22c55e" />

        {/* IA -> Boxes */}
        <path className="av-conector" data-ordem={3} d="M510 200 L540 200" stroke="rgba(34,197,94,0.6)" strokeWidth="2" fill="none" />
        <circle className="av-conector" data-ordem={3} cx="525" cy="200" r="3" fill="#22c55e" />

        {/* Boxes -> Decisao */}
        <path className="av-conector" data-ordem={4} d="M790 200 L815 200" stroke="rgba(34,197,94,0.5)" strokeWidth="2" strokeDasharray="4 4" fill="none" />

        {/* Decisao -> Reserva (SIM) */}
        <path className="av-conector" data-ordem={6} d="M985 200 L1010 200" stroke="rgba(34,197,94,0.5)" strokeWidth="2" strokeDasharray="4 4" fill="none" />

        {/* Reserva -> Cliente busca */}
        <path className="av-conector" data-ordem={8} d="M1220 200 L1240 200" stroke="rgba(34,197,94,0.6)" strokeWidth="2" fill="none" />
        <circle className="av-conector" data-ordem={8} cx="1230" cy="200" r="3" fill="#22c55e" />

        {/* Decisao -> Sugere outro horario (NAO) */}
        <path className="av-conector" data-ordem={5} d="M900 285 Q 900 330, 780 360" stroke="rgba(239,68,68,0.55)" strokeWidth="2" strokeDasharray="5 5" fill="none" />

        {/* Reserva -> Equipe executa */}
        <path className="av-conector" data-ordem={7} d="M1115 320 L1115 360" stroke="rgba(34,197,94,0.5)" strokeWidth="2" strokeDasharray="4 4" fill="none" />
        <polygon className="av-conector" data-ordem={7} points="1109,355 1115,365 1121,355" fill="rgba(34,197,94,0.7)" />

        {/* Pill SIM */}
        <rect className="av-conector" data-ordem={6} x="975" y="180" width="34" height="20" rx="10" fill="rgba(15,20,18,0.95)" stroke="rgba(34,197,94,0.6)" strokeWidth="1" />
        <text className="av-conector" data-ordem={6} x="992" y="194" fontSize="11" fontWeight="700" fill="#6ee7a0" textAnchor="middle">Sim</text>

        {/* Pill NAO */}
        <rect className="av-conector" data-ordem={5} x="855" y="305" width="42" height="22" rx="11" fill="rgba(15,20,18,0.95)" stroke="rgba(239,68,68,0.6)" strokeWidth="1" />
        <text className="av-conector" data-ordem={5} x="876" y="320" fontSize="11" fontWeight="700" fill="#fca5a5" textAnchor="middle">Não</text>
      </svg>

      {/* === 1. CLIENTE pede horario === */}
      <div className="absolute av-bloco" data-ordem={1} style={{ left: 20, top: 40, width: 200, height: 360 }}>
        <div className="relative mx-auto" style={{ width: 150, height: 150 }}>
          <div
            className="h-full w-full overflow-hidden rounded-full"
            style={{
              border: "3px solid rgba(34,197,94,0.3)",
              boxShadow: "0 12px 30px -8px rgba(0,0,0,0.7), 0 0 30px -8px rgba(34,197,94,0.25)",
            }}
          >
            <img src="/foto-cliente-lavajato.webp" alt="" loading="lazy" className="h-full w-full object-cover" />
          </div>
          <div className="absolute -top-1 -left-1 flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366] shadow-lg">
            <WhatsAppIcon className="h-6 w-6 text-white" />
          </div>
        </div>
        <div className="mt-4 text-center">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Cliente</div>
          <div className="mt-1 text-[16px] font-bold text-white">Pede horário</div>
        </div>
        <div className="mt-4 rounded-xl px-3 py-2.5" style={CAIXA_BASE}>
          <div className="text-[11.5px] text-zinc-300 leading-snug text-center">
            "Quero lavar meu carro amanhã"
          </div>
        </div>
      </div>

      {/* === 2. IA monta o servico === */}
      <Card className="av-bloco" data-ordem={2} x={230} y={80} w={280} h={240}>
        <div className="flex flex-col h-full p-4">
          <div className="flex items-start gap-3 mb-3">
            <img src="/robo-01-cruzado.webp" alt="" loading="lazy" className="h-14 w-14 shrink-0 object-contain" />
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-bold text-white leading-tight">IA do Evolzap</div>
              <div className="inline-flex items-center gap-1.5 mt-2 px-2 py-0.5 rounded-full bg-green-500/20 border border-green-500/30">
                <span className="h-1.5 w-1.5 rounded-full bg-green-400" />
                <span className="text-[9px] font-bold text-green-300 uppercase tracking-wider">Monta o serviço</span>
              </div>
            </div>
          </div>
          <div className="rounded-xl bg-white/[0.04] border border-white/10 px-3 py-3">
            <div className="text-[11.5px] text-zinc-200 leading-relaxed mb-2">"Qual serviço você deseja?"</div>
            <div className="flex flex-wrap gap-1">
              <span className="text-[9.5px] font-medium px-2 py-0.5 rounded-full bg-white/5 text-zinc-400 border border-white/10">Simples</span>
              <span className="text-[9.5px] font-bold px-2 py-0.5 rounded-full bg-green-500/25 text-green-300 border border-green-500/50">Completa ✓</span>
              <span className="text-[9.5px] font-medium px-2 py-0.5 rounded-full bg-white/5 text-zinc-400 border border-white/10">Polimento</span>
            </div>
          </div>
        </div>
      </Card>

      {/* === 3. Verifica boxes disponiveis === */}
      <Card className="av-bloco" data-ordem={3} x={540} y={80} w={250} h={240}>
        <div className="flex flex-col h-full p-4">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-500/15 border border-green-500/30">
              <CalendarDays className="h-4 w-4 text-green-400" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-bold text-white leading-tight">Verifica boxes</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">Lavagem completa · Box 2</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              { hora: "09:00" },
              { hora: "10:00", alvo: true },
              { hora: "11:00", livre: false },
              { hora: "14:00" },
            ].map((h) => (
              <div
                key={h.hora}
                className={`flex items-center justify-center rounded-md border px-2 py-1.5 text-[11px] font-medium ${
                  h.alvo
                    ? "bg-green-500/20 border-green-500/50 text-green-300"
                    : h.livre === false
                    ? "bg-white/[0.02] border-white/10 text-zinc-600 line-through"
                    : "bg-white/5 border-white/10 text-zinc-300"
                }`}
              >
                {h.hora}
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* === 4. DECISAO — LOSANGO === */}
      <div
        className="absolute av-bloco"
        data-ordem={4}
        style={{
          left: 840, top: 140, width: 120, height: 120,
          transform: "rotate(45deg)",
          background: "linear-gradient(160deg, rgba(28,33,32,0.95) 0%, rgba(16,20,19,0.98) 100%)",
          border: "1px solid rgba(245,158,11,0.5)",
          borderRadius: "12px",
          boxShadow: "0 20px 44px -18px rgba(0,0,0,0.95), 0 0 25px -8px rgba(245,158,11,0.35)",
        }}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 p-2" style={{ transform: "rotate(-45deg)" }}>
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/15 border border-amber-500/30">
            <HelpCircle className="h-4 w-4 text-amber-400" />
          </div>
          <div className="text-[8.5px] font-bold uppercase tracking-[0.14em] text-amber-400">Decisão</div>
          <div className="text-[10px] font-bold text-white leading-tight text-center">Box<br />disponível?</div>
        </div>
      </div>

      {/* === 5. Reserva confirmada (ATIVA verde) === */}
      <Card className="av-bloco" data-ordem={6} x={1010} y={80} w={210} h={240} ativa>
        <div className="flex flex-col h-full p-3.5">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-500/25 border border-green-500/50">
              <CalendarCheck className="h-4 w-4 text-green-300" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-green-400">Reserva</div>
              <div className="text-[13px] font-bold text-white leading-tight">Box 2 reservado</div>
            </div>
          </div>
          <div className="text-[10px] text-zinc-400 leading-snug mb-3">Lavagem completa agendada.</div>
          <div className="mt-auto rounded-lg bg-white/5 border border-white/10 px-2.5 py-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10.5px] text-zinc-400">Amanhã</span>
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-green-500">
                <Check className="h-3 w-3 text-black" strokeWidth={3} />
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[13px] font-bold text-white">10:00</span>
              <span className="ml-auto px-1.5 py-0.5 rounded bg-green-500/25 text-[9px] font-bold text-green-300 uppercase">R$ 80</span>
            </div>
          </div>
        </div>
      </Card>

      {/* === 6. CLIENTE BUSCA CARRO (foto + selo) === */}
      <div className="absolute av-bloco" data-ordem={8} style={{ left: 1240, top: 40, width: 250, height: 360 }}>
        <div className="absolute" style={{ left: 0, top: 100, width: 90 }}>
          <div className="text-[14px] font-bold text-white leading-tight mb-2">Cliente busca</div>
          <div className="text-[10.5px] text-zinc-400 leading-snug">Carro pronto e cliente avisado.</div>
        </div>
        <div
          className="absolute overflow-hidden"
          style={{
            left: 100, top: 0, width: 150, height: 340,
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 20px 44px -18px rgba(0,0,0,0.95)",
          }}
        >
          <img src="/foto-carro-limpo.webp" alt="" loading="lazy" className="h-full w-full object-cover" />
          <div className="absolute bottom-2 left-2 right-2 rounded-lg bg-black/85 backdrop-blur-sm border border-green-500/30 px-2 py-1.5">
            <div className="flex items-center gap-1.5">
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-green-500">
                <Sparkles className="h-2.5 w-2.5 text-black" strokeWidth={3} />
              </span>
              <span className="text-[10px] font-bold text-white leading-tight">Pronto! 🚗</span>
            </div>
            <div className="flex items-center gap-0.5 mt-0.5">
              {[1,2,3,4,5].map(i => <Star key={i} className="h-2.5 w-2.5 text-yellow-400 fill-yellow-400" />)}
            </div>
          </div>
        </div>
      </div>

      {/* === 7. Sugere outro horario (ramo NAO) === */}
      <Card className="av-bloco" data-ordem={5} x={510} y={360} w={380} h={130}>
        <div className="flex flex-col h-full p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 border border-white/10">
              <Search className="h-3.5 w-3.5 text-zinc-400" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-bold text-white leading-tight">Sugere outro horário</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">Oferece os próximos boxes livres.</div>
            </div>
          </div>
          <div className="rounded-lg bg-white/5 border border-white/10 px-2.5 py-2 mt-auto">
            <div className="text-[10.5px] text-zinc-300 leading-snug">
              "10h lotou. Consigo às 09:00 ou 14:00, tudo bem?"
            </div>
          </div>
        </div>
      </Card>

      {/* === 8. Equipe executa === */}
      <Card className="av-bloco" data-ordem={7} x={920} y={360} w={380} h={130}>
        <div className="flex flex-col h-full p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-500/15 border border-purple-500/30">
              <Droplets className="h-4 w-4 text-purple-300" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-bold text-white leading-tight">Equipe executa</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">Serviço em andamento no Box 2.</div>
            </div>
          </div>
          <div className="rounded-lg bg-white/5 border border-white/10 px-2.5 py-1.5 mt-auto flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-amber-500/15 border border-amber-500/30">
              <Car className="h-3 w-3 text-amber-400" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold text-white leading-tight">Novo serviço</div>
              <div className="text-[10px] text-zinc-500 truncate">Marcos A. — Lavagem completa, 10:00</div>
            </div>
          </div>
        </div>
      </Card>
    </PalcoEscalavel>
  );
}
